import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { paymentApi } from "configs/api";

export interface Payment {
  _id: string;
  userID: string;
  amount: number;
  currency: string;
  status: "pending" | "completed" | "failed" | "refunded";
  paymentMethod: string;
  paymentIntentID?: string;
  courseID?: string;
  subscriptionID?: string;
  /** Backend payment row type: course | subscription | credits */
  type?: string;
  courseTitle?: string | null;
  createdAt: string;
}

export interface Subscription {
  _id: string;
  userID: string;
  planID: string;
  status: "active" | "cancelled" | "expired";
  startDate: string;
  endDate?: string;
  autoRenew: boolean;
}

interface PaymentState {
  payments: Payment[];
  subscriptions: Subscription[];
  loading: boolean;
  error: string | null;
  paymentIntent: any | null;
}

const initialState: PaymentState = {
  payments: [],
  subscriptions: [],
  loading: false,
  error: null,
  paymentIntent: null,
};

// Async thunks
export const createPaymentIntent = createAsyncThunk(
  "payment/createIntent",
  async (data: { amount: number; currency?: string; courseID?: string }) => {
    const response = await paymentApi.post("/api/payments/intent", data);
    return response.data?.data || response.data;
  }
);

export const purchaseCourse = createAsyncThunk(
  "payment/purchaseCourse",
  async (data: { courseID: string; paymentMethodID: string }) => {
    const response = await paymentApi.post("/api/payments/course/purchase", data);
    return response.data?.data || response.data;
  }
);

export const createSubscription = createAsyncThunk(
  "payment/createSubscription",
  async (data: { planID: string; paymentMethodID: string }) => {
    const response = await paymentApi.post("/api/payments/subscription", data);
    return response.data?.data || response.data;
  }
);

export const fetchUserPayments = createAsyncThunk(
  "payment/fetchUser",
  async (params?: { limit?: number; page?: number }) => {
    const response = await paymentApi.get("/api/payments/history", {
      params: { limit: params?.limit ?? 500, page: params?.page ?? 1 },
    });
    return response.data?.data || response.data || [];
  }
);

export const confirmPayment = createAsyncThunk(
  "payment/confirm",
  async (data: { paymentIntentID: string }) => {
    const response = await paymentApi.post("/api/payments/confirm", data);
    return response.data?.data || response.data;
  }
);

export const cancelSubscription = createAsyncThunk(
  "payment/cancelSubscription",
  async (id: string) => {
    await paymentApi.delete(`/api/payments/subscription/${id}`);
    return id;
  }
);

const paymentSlice = createSlice({
  name: "payment",
  initialState,
  reducers: {
    clearPaymentIntent: (state) => {
      state.paymentIntent = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createPaymentIntent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPaymentIntent.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentIntent = action.payload;
      })
      .addCase(createPaymentIntent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create payment intent";
      })
      .addCase(purchaseCourse.fulfilled, (state, action) => {
        state.payments.push(action.payload);
      })
      .addCase(createSubscription.fulfilled, (state, action) => {
        state.subscriptions.push(action.payload);
      })
      .addCase(fetchUserPayments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserPayments.fulfilled, (state, action) => {
        state.loading = false;
        state.payments = action.payload;
      })
      .addCase(fetchUserPayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to load payments";
      })
      .addCase(confirmPayment.fulfilled, (state, action) => {
        const index = state.payments.findIndex(
          (p) => p.paymentIntentID === action.payload.paymentIntentID
        );
        if (index !== -1) {
          state.payments[index] = action.payload;
        }
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.subscriptions = state.subscriptions.filter((s) => s._id !== action.payload);
      });
  },
});

export const { clearPaymentIntent, clearError } = paymentSlice.actions;
export default paymentSlice.reducer;

